import { apiService } from "./api";
import { ProductImage } from "../types/productImage";

// Types for Procurement
export interface Supplier {
    supplier_id: string;
    name: string;
    contact_person?: string;
    email?: string;
    phone?: string;
    address?: string;
    payment_terms?: string;
    is_active: boolean;
    create_time: string;
    update_time: string;
}

export interface PurchaseItem {
    item_id?: string;
    product_id: string;
    quantity_ordered: number;
    quantity_received: number;
    quantity_remaining?: number;
    unit_cost: number;
    total_cost?: number;
    is_gift?: boolean;
    bonus_quantity?: number;
    requested_batch_info?: string;
    quality_check_required: boolean;
    quality_check_status?: string;
    quality_check_notes?: string;
    received_date?: string;
    received_by_user_id?: string;
    received_location?: string;
    notes?: string;
    product?: {
        product_id: string;
        name: string;
        sku: string;
        cost_price: number;
        selling_price: number;
        category?: { name: string };
        brand?: { name: string };
        stock?: {
            quantity: number;
            available_quantity: number;
            reserved_quantity: number;
            is_low_stock: boolean;
        };
        images?: ProductImage[];
    };
    batches?: PurchaseBatch[];
}

export interface PurchasePayment {
    payment_id: string;
    purchase_id: string;
    amount: number;
    payment_method: string;
    payment_date: string;
    reference_number?: string;
    notes?: string;
    account_id?: string | null;
    created_by_user_id?: string;
    create_time: string;
    update_time: string;
}

// A payment plus enough of its parent purchase to display in a
// cross-purchase list — returned by GET /suppliers/payments/search.
export interface PurchasePaymentWithPurchase extends PurchasePayment {
    purchase_number: string;
    supplier_name?: string;
}

export interface PurchasePaymentCreate {
    amount: number;
    payment_method: string;
    payment_date?: string;
    reference_number?: string;
    notes?: string;
    account_id?: string | null;
}

export interface Purchase {
    purchase_id: string;
    purchase_number: string;
    supplier_id: string;
    status:
        | "PENDING"
        | "CONFIRMED"
        | "RECEIVED"
        | "PARTIAL_RECEIVED"
        | "CANCELLED"
        | "ON_HOLD";
    payment_status: "UNPAID" | "PARTIAL" | "PAID";
    total_amount: number;
    discount_amount: number;
    tax_amount: number;
    reference_number?: string;
    payment_terms?: string;
    shipping_cost: number;
    expected_delivery_date?: string;
    notes?: string;
    purchase_date: string;
    received_date?: string;
    supplier?: Supplier;
    items: PurchaseItem[];
    approvals?: PurchaseApproval[];
    payment_transactions?: PurchasePayment[];
    net_payable?: number;
    amount_paid?: number;
    amount_due?: number;
    create_time: string;
    update_time: string;
}

/** A row of the Purchase Orders list: the order's own fields and totals. */
export interface PurchaseListRow
    extends Omit<Purchase, "items" | "approvals" | "payment_transactions" | "supplier"> {
    supplier?: { supplier_id: string; name: string } | null;
    item_count: number;
    /** Σ quantity received × unit cost across the order's lines. */
    received_value: number;
}

export interface PurchaseListPage {
    items: PurchaseListRow[];
    total: number;
    /** Past expected delivery, not received/cancelled (within the status filter). */
    overdue_count: number;
}

export interface PurchaseSearchQuery {
    skip?: number;
    limit?: number;
    q?: string;
    status?: string;
    supplier_id?: string;
    product_ids?: string[];
    date_from?: string;
    date_to?: string;
    expected_delivery_from?: string;
    expected_delivery_to?: string;
    reference_number?: string;
    sort_by?: string;
    sort_order?: "asc" | "desc";
}

export interface PurchaseStats {
    counts_by_status: Record<string, number>;
    total_count: number;
    /** Net payable of every order that isn't cancelled. */
    total_value: number;
}

export interface PurchaseBatch {
    batch_id: string;
    purchase_item_id: string;
    batch_number: string;
    expiry_date?: string;
    manufacturing_date?: string;
    quantity: number;
    unit_cost: number;
    location?: string;
    create_time: string;
    update_time: string;
}

// expiry_date is optional and the only thing staff enter at receiving time —
// when present, the backend auto-creates a batch (batch number generated
// server-side) so a line becomes expiry-trackable with zero extra data entry.
export interface ReceiveItemPayload {
    quantity: number;
    expiry_date?: string;
}

export interface PurchaseApproval {
    approval_id: string;
    purchase_id: string;
    approver_user_id: string;
    approval_level: number;
    status: "PENDING" | "APPROVED" | "REJECTED";
    approval_date?: string;
    comments?: string;
    create_time: string;
    update_time: string;
}

export interface CreateSupplierRequest {
    name: string;
    contact_person?: string;
    email?: string;
    phone?: string;
    address?: string;
    payment_terms?: string;
}

export interface UpdateSupplierRequest extends Partial<CreateSupplierRequest> {
    is_active?: boolean;
}

export interface CreatePurchaseRequest {
    supplier_id: string;
    total_amount: number;
    discount_amount?: number;
    tax_amount?: number;
    items: {
        product_id: string;
        quantity_ordered: number;
        quantity_received?: number;
        quantity_remaining?: number;
        unit_cost: number;
        total_cost: number;
        is_gift?: boolean;
        bonus_quantity?: number;
        quality_check_required?: boolean;
        requested_batch_info?: string;
        notes?: string;
    }[];
    reference_number?: string;
    payment_terms?: string;
    shipping_cost?: number;
    expected_delivery_date?: string;
    notes?: string;
}

export interface UpdatePurchaseRequest {
    reference_number?: string;
    payment_terms?: string;
    shipping_cost?: number;
    expected_delivery_date?: string;
    notes?: string;
    total_amount?: number;
    discount_amount?: number;
    tax_amount?: number;
    items?: Array<{
        product_id: string;
        quantity_ordered: number;
        unit_cost: number;
        total_cost: number;
        is_gift?: boolean;
        bonus_quantity?: number;
        quality_check_required?: boolean;
        requested_batch_info?: string;
        notes?: string;
    }>;
}

export interface CreateBatchRequest {
    // Omit it — the backend auto-generates one. Staff only ever give us
    // an expiry date, never a batch number.
    batch_number?: string;
    expiry_date?: string;
    manufacturing_date?: string;
    quantity: number;
    unit_cost: number;
    location?: string;
}

// Procurement Service Class
class ProcurementService {
    // Supplier Management
    async getSuppliers(params?: {
        skip?: number;
        limit?: number;
        search?: string;
        active_only?: boolean;
    }): Promise<Supplier[]> {
        return await apiService.get<Supplier[]>("/suppliers", { params });
    }

    async getSupplier(supplierId: string): Promise<Supplier> {
        return await apiService.get<Supplier>(`/suppliers/${supplierId}`);
    }

    async createSupplier(supplier: CreateSupplierRequest): Promise<Supplier> {
        return await apiService.post<Supplier>("/suppliers", supplier);
    }

    async updateSupplier(
        supplierId: string,
        supplier: UpdateSupplierRequest,
    ): Promise<Supplier> {
        return await apiService.put<Supplier>(
            `/suppliers/${supplierId}`,
            supplier,
        );
    }

    async deleteSupplier(supplierId: string): Promise<void> {
        await apiService.delete(`/suppliers/${supplierId}`);
    }

    // Purchase Management
    async getPurchases(params?: {
        skip?: number;
        limit?: number;
        status?: string;
        supplier_id?: string;
    }): Promise<Purchase[]> {
        return await apiService.get<Purchase[]>("/suppliers/purchases", {
            params,
        });
    }

    /** One lean, filtered, sorted page of the Purchase Orders list. Rows
     * carry totals but no item/product detail — open an order with
     * getPurchase() for that. */
    async searchPurchases(params: PurchaseSearchQuery): Promise<PurchaseListPage> {
        return await apiService.get<PurchaseListPage>("/suppliers/purchases/search", {
            params: {
                ...params,
                product_ids: params.product_ids?.length ? params.product_ids.join(",") : undefined,
            },
        });
    }

    /** Counts per status and total value of all non-cancelled orders. */
    async getPurchaseStats(): Promise<PurchaseStats> {
        return await apiService.get<PurchaseStats>("/suppliers/purchases/stats");
    }

    async getPurchase(purchaseId: string): Promise<Purchase> {
        return await apiService.get<Purchase>(
            `/suppliers/purchases/${purchaseId}`,
        );
    }

    async getPurchaseByNumber(purchaseNumber: string): Promise<Purchase> {
        return await apiService.get<Purchase>(
            `/suppliers/purchases/number/${purchaseNumber}`,
        );
    }

    async createPurchase(purchase: CreatePurchaseRequest): Promise<Purchase> {
        return await apiService.post<Purchase>(
            "/suppliers/purchases",
            purchase,
        );
    }

    async updatePurchase(
        purchaseId: string,
        purchase: UpdatePurchaseRequest,
    ): Promise<Purchase> {
        return await apiService.put<Purchase>(
            `/suppliers/purchases/${purchaseId}`,
            purchase,
        );
    }

    async updatePurchaseStatus(
        purchaseId: string,
        status: string,
    ): Promise<Purchase> {
        return await apiService.patch<Purchase>(
            `/suppliers/purchases/${purchaseId}/status`,
            { status },
        );
    }

    async receivePurchaseItems(
        purchaseId: string,
        itemReceipts: Record<string, ReceiveItemPayload>,
    ): Promise<Purchase> {
        return await apiService.post<Purchase>(
            `/suppliers/purchases/${purchaseId}/receive`,
            itemReceipts,
        );
    }

    async deletePurchase(purchaseId: string): Promise<void> {
        await apiService.delete(`/suppliers/purchases/${purchaseId}`);
    }

    async getPurchaseItems(purchaseId: string): Promise<PurchaseItem[]> {
        return await apiService.get<PurchaseItem[]>(
            `/suppliers/purchases/${purchaseId}/items`,
        );
    }

    // Status-specific queries
    async getPendingPurchases(params?: {
        skip?: number;
        limit?: number;
    }): Promise<Purchase[]> {
        return await apiService.get<Purchase[]>(
            "/suppliers/purchases/status/pending",
            { params },
        );
    }

    async getConfirmedPurchases(params?: {
        skip?: number;
        limit?: number;
    }): Promise<Purchase[]> {
        return await apiService.get<Purchase[]>(
            "/suppliers/purchases/status/confirmed",
            { params },
        );
    }

    async getReceivedPurchases(params?: {
        skip?: number;
        limit?: number;
    }): Promise<Purchase[]> {
        return await apiService.get<Purchase[]>(
            "/suppliers/purchases/status/received",
            { params },
        );
    }

    async getPurchasesByDateRange(
        startDate: string,
        endDate: string,
        params?: { skip?: number; limit?: number },
    ): Promise<Purchase[]> {
        return await apiService.get<Purchase[]>(
            "/suppliers/purchases/date-range",
            {
                params: { start_date: startDate, end_date: endDate, ...params },
            },
        );
    }

    // Batch Management
    async getExpiringBatches(daysAhead: number = 30): Promise<PurchaseBatch[]> {
        return await apiService.get<PurchaseBatch[]>(
            "/suppliers/batches/expiring",
            {
                params: { days_ahead: daysAhead },
            },
        );
    }

    async getBatch(batchId: string): Promise<PurchaseBatch> {
        return await apiService.get<PurchaseBatch>(
            `/suppliers/batches/${batchId}`,
        );
    }

    async createBatch(
        purchaseItemId: string,
        batch: CreateBatchRequest,
    ): Promise<PurchaseBatch> {
        return await apiService.post<PurchaseBatch>(
            "/suppliers/batches",
            batch,
            {
                params: { purchase_item_id: purchaseItemId },
            },
        );
    }

    async updateBatch(
        batchId: string,
        batch: Partial<CreateBatchRequest>,
    ): Promise<PurchaseBatch> {
        return await apiService.put<PurchaseBatch>(
            `/suppliers/batches/${batchId}`,
            batch,
        );
    }

    async deleteBatch(batchId: string): Promise<void> {
        await apiService.delete(`/suppliers/batches/${batchId}`);
    }

    // Quality Check
    async getItemsRequiringQualityCheck(params?: {
        skip?: number;
        limit?: number;
    }): Promise<PurchaseItem[]> {
        return await apiService.get<PurchaseItem[]>(
            "/suppliers/items/quality-check",
            { params },
        );
    }

    // Approval Management
    async getApproval(approvalId: string): Promise<PurchaseApproval> {
        return await apiService.get<PurchaseApproval>(
            `/suppliers/approvals/${approvalId}`,
        );
    }

    async approvePurchase(
        approvalId: string,
        comments?: string,
    ): Promise<PurchaseApproval> {
        return await apiService.post<PurchaseApproval>(
            `/suppliers/approvals/${approvalId}/approve`,
            { comments },
        );
    }

    async rejectPurchase(
        approvalId: string,
        comments?: string,
    ): Promise<PurchaseApproval> {
        return await apiService.post<PurchaseApproval>(
            `/suppliers/approvals/${approvalId}/reject`,
            { comments },
        );
    }

    async createApproval(
        purchaseId: string,
        approverUserId: string,
        approvalLevel: number,
        comments?: string,
    ): Promise<PurchaseApproval> {
        return await apiService.post<PurchaseApproval>(
            "/suppliers/approvals",
            null,
            {
                params: {
                    purchase_id: purchaseId,
                    approver_user_id: approverUserId,
                    approval_level: approvalLevel,
                    comments,
                },
            },
        );
    }

    // ── Purchase Payments ──────────────────────────────────────────────────

    async listPurchasePayments(purchaseId: string): Promise<PurchasePayment[]> {
        return await apiService.get<PurchasePayment[]>(
            `/suppliers/purchases/${purchaseId}/payments`,
        );
    }

    async createPurchasePayment(
        purchaseId: string,
        payment: PurchasePaymentCreate,
    ): Promise<PurchasePayment> {
        return await apiService.post<PurchasePayment>(
            `/suppliers/purchases/${purchaseId}/payments`,
            payment,
        );
    }

    async deletePurchasePayment(
        purchaseId: string,
        paymentId: string,
    ): Promise<void> {
        return await apiService.delete(
            `/suppliers/purchases/${purchaseId}/payments/${paymentId}`,
        );
    }

    // All supplier payments made within a date range, across every purchase
    // order — for "what did we pay suppliers today" style reports.
    // dateFromIso/dateToIso must be full ISO instants with a timezone
    // offset (e.g. from Date.toISOString()) — see DailyCashFlowAnalyzer for
    // why a bare YYYY-MM-DD isn't precise enough here.
    async searchPurchasePayments(
        dateFromIso: string,
        dateToIso: string,
    ): Promise<PurchasePaymentWithPurchase[]> {
        return await apiService.get<PurchasePaymentWithPurchase[]>(
            "/suppliers/payments/search",
            { params: { date_from: dateFromIso, date_to: dateToIso } },
        );
    }
}

export const procurementService = new ProcurementService();
